import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';
import CurrencySwitcher from './CurrencySwitcher';
import { withBase } from '@/constants';

interface NavItem {
  key: string;
  href?: string;
  to?: string;
}

const NAV_ITEMS: NavItem[] = [
  { key: 'nav.home', href: withBase('/#hero') },
  { key: 'nav.garage', href: withBase('/#garaj') },
  { key: 'nav.villa', href: withBase('/#villa') },
  { key: 'nav.winter', href: withBase('/#kis-bahcesi') },
  { key: 'nav.projects', href: withBase('/#projeler') },
  { key: 'nav.about', href: withBase('/#hakkimizda') },
  { key: 'nav.dealer', to: '/bayilik' },
  { key: 'nav.contact', href: withBase('/#iletisim') },
];

const linkClass =
  'whitespace-nowrap text-sm font-medium text-background-100/90 transition-colors hover:text-primary-400';

export default function Header() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 z-50 w-full transition-all duration-500 ${
        scrolled
          ? 'bg-foreground-950/95 backdrop-blur-md border-b border-primary-500/20 py-2'
          : 'bg-transparent py-3'
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <Link to="/" aria-label="VILLAGARAGE" className="flex shrink-0 items-center">
          <img
            src="https://storage.helloreaddy.io/project_files/23cbf37b-3b60-45d6-b458-2fbb3e9ec10a/38c06273-6207-48ae-a08c-ac1e41dc7367_compressed_logo.webp"
            alt="VILLAGARAGE Premium Garage Systems"
            className="h-11 w-auto object-contain md:h-12"
          />
        </Link>

        <nav className="hidden xl:flex items-center gap-5 2xl:gap-7">
          {NAV_ITEMS.map((item) =>
            item.to ? (
              <Link key={item.key} to={item.to} className={linkClass}>
                {t(item.key)}
              </Link>
            ) : (
              <a key={item.key} href={item.href} className={linkClass}>
                {t(item.key)}
              </a>
            ),
          )}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          <LanguageSwitcher dark />
          <CurrencySwitcher dark />
          <Link
            to="/giris"
            className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-background-100/90 transition-colors hover:text-primary-400"
          >
            <i className="ri-user-line text-base" aria-hidden="true"></i>
            {t('nav.login')}
          </Link>
          <a
            href={withBase('/#iletisim')}
            className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400"
          >
            {t('nav.cta')}
          </a>
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <LanguageSwitcher dark />
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            className="flex h-10 w-10 items-center justify-center rounded-md text-background-50 cursor-pointer"
          >
            <i
              className={`text-2xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}
              aria-hidden="true"
            ></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden border-t border-foreground-700/40 bg-foreground-950/98 backdrop-blur-md px-4 py-6">
          <nav className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) =>
              item.to ? (
                <Link
                  key={item.key}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md px-3 py-3 text-base font-medium text-background-100 transition-colors hover:bg-foreground-800 hover:text-primary-400"
                >
                  {t(item.key)}
                </Link>
              ) : (
                <a
                  key={item.key}
                  href={item.href}
                  onClick={() => setMobileOpen(false)}
                  className="rounded-md px-3 py-3 text-base font-medium text-background-100 transition-colors hover:bg-foreground-800 hover:text-primary-400"
                >
                  {t(item.key)}
                </a>
              ),
            )}
          </nav>
          <div className="mt-4 flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <CurrencySwitcher dark />
              <Link
                to="/giris"
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-background-100/90"
              >
                <i className="ri-user-line text-base" aria-hidden="true"></i>
                {t('nav.login')}
              </Link>
            </div>
            <a
              href={withBase('/#iletisim')}
              onClick={() => setMobileOpen(false)}
              className="whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-center text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400"
            >
              {t('nav.cta')}
            </a>
          </div>
        </div>
      )}
    </header>
  );
}